import _ from 'lodash'
import type { Moment } from 'moment-timezone'
import { isNumber } from 'remeda'
import {
  AggregateLienWaiverStatus,
  LegalDocumentStatus,
  LienWaiverCategory,
  LienWaiverType,
  VendorLienWaiverReminderFrequency,
} from '../enums.js'
import { dollarNumberFormat, dollarsToCents, formatCentsToDollars } from './numbers.js'

export const DEFAULT_VENDOR_LIEN_WAIVER_REMINDER_FREQUENCY =
  VendorLienWaiverReminderFrequency.WEEKLY

export enum LienWaiverProgressType {
  PROGRESS = 'progress',
  FINAL = 'final',
}

export const conditionalLienWaiverTypes = [
  LienWaiverType.CONDITIONAL_FINAL_PAYMENT,
  LienWaiverType.CONDITIONAL_PROGRESS_PAYMENT,
]

export const unconditionalLienWaiverTypes = [
  LienWaiverType.UNCONDITIONAL_FINAL_PAYMENT,
  LienWaiverType.UNCONDITIONAL_PROGRESS_PAYMENT,
]

export const lienWaiverCategoryForType = (type: LienWaiverType): LienWaiverCategory =>
  conditionalLienWaiverTypes.includes(type)
    ? LienWaiverCategory.CONDITIONAL
    : LienWaiverCategory.UNCONDITIONAL

/**
 * Formats a lien waiver amount to insert in a form annotation value.
 * 4 cases:
 *  - Amount only: "$1,000"
 *  - Text only: "Paid to date"
 *  - Amount + text: "$1,000 - Paid to date"
 *  - None of the above: ""
 */
export function formatLienWaiverAmount({
  amount,
  text,
  withCurrency,
}: {
  amount?: number
  text?: string | null
  withCurrency: boolean
}): string {
  const hasAmount = isNumber(amount)
  const cleanText = _.trim(text ?? '')
  const hasText = cleanText.length > 0

  // Amount + text
  if (hasAmount && hasText) {
    return `${withCurrency ? '$' : ''}${dollarNumberFormat.format(amount / 100)} - ${cleanText}`
  }

  // Amount only
  if (hasAmount) {
    return `${withCurrency ? '$' : ''}${dollarNumberFormat.format(amount / 100)}`
  }

  // Text only
  if (hasText) {
    return cleanText
  }

  // Nothing
  return ''
}

/**
 * Returns a description of the amount due or paid for an AP invoice.
 * For example:
 *  - $1,000 due
 *  - $1,000 paid
 */
export function lienWaiverInvoiceAmountDescription({
  invoice,
  category,
}: {
  invoice: {
    isNetFullyPaid: boolean
    isGrossFullyPaid: boolean
    grossAmount: number
    amountPaid: number
    retentionAmount: number
  }
  category: LienWaiverCategory
}): string {
  const invoiceAmount = lienWaiverInvoiceAmountForCategory({ invoice, category })
  const formattedAmount = formatCentsToDollars(invoiceAmount, true)

  switch (category) {
    case LienWaiverCategory.CONDITIONAL:
      return `${formattedAmount} due`
    case LienWaiverCategory.UNCONDITIONAL:
      return `${formattedAmount} paid`
  }
}

export function apInvoiceNetAmount(invoice: {
  grossAmount: number
  retentionAmount: number
}): number {
  return invoice.grossAmount - invoice.retentionAmount
}

export function apInvoiceAmountPaid(invoice: { amountPaid: number }): number {
  return invoice.amountPaid
}

export function apInvoiceNetAmountDue(invoice: {
  isNetFullyPaid: boolean
  isGrossFullyPaid: boolean
  grossAmount: number
  amountPaid: number
  retentionAmount: number
}): number {
  // NOTE this was added to handle discounts on invoices
  const amountDue = invoice.isNetFullyPaid
    ? 0
    : apInvoiceNetAmount(invoice) - apInvoiceAmountPaid(invoice)
  return amountDue
}

export function lienWaiverInvoiceAmountForCategory({
  invoice,
  category,
}: {
  invoice: {
    isNetFullyPaid: boolean
    isGrossFullyPaid: boolean
    grossAmount: number
    amountPaid: number
    retentionAmount: number
  }
  category: LienWaiverCategory
}): number {
  switch (category) {
    case LienWaiverCategory.CONDITIONAL:
      return apInvoiceNetAmountDue(invoice)
    case LienWaiverCategory.UNCONDITIONAL:
      return apInvoiceAmountPaid(invoice)
  }
}

// Return the copy to use to describe a lien waiver type
export function stringForLienWaiverType(type: LienWaiverType): string {
  switch (type) {
    case LienWaiverType.CONDITIONAL_PROGRESS_PAYMENT:
      return 'Conditional Progress'
    case LienWaiverType.CONDITIONAL_FINAL_PAYMENT:
      return 'Conditional Final'
    case LienWaiverType.UNCONDITIONAL_PROGRESS_PAYMENT:
      return 'Unconditional Progress'
    case LienWaiverType.UNCONDITIONAL_FINAL_PAYMENT:
      return 'Unconditional Final'
  }
}

/** Returns a default subject line for a vendor lien waiver request */
function defaultLienWaiverRequestSubject({
  projectNumber,
  projectName,
  type,
  throughDate,
  subcontractorName,
  subTierVendorName,
}: {
  projectNumber: string
  projectName: string
  type: LienWaiverType
  throughDate: Moment
  subcontractorName: string
  subTierVendorName?: string
}): string {
  const formattedThroughDate = throughDate.format('MM/D/YY')
  const lienWaiverType = stringForLienWaiverType(type)
  const subTierInsertion = subTierVendorName ? ` ${subTierVendorName}` : ''
  const baseSubject = `${projectNumber} ${projectName} -${subTierInsertion} ${lienWaiverType} through ${formattedThroughDate} requested by ${subcontractorName}`
  return baseSubject
}

const REMINDER_PREFIX = '[Reminder]'
const REJECTED_PREFIX = '[Needs action]'

/** Returns the subject line to use for a lien waiver request */
export function getLienWaiverRequestSubject({
  lastSubject,
  isReminder,
  isRejected,
  projectNumber,
  projectName,
  type,
  throughDate,
  subcontractorName,
  subTierVendorName,
}: {
  lastSubject: string | null
  isReminder: boolean
  isRejected: boolean
  projectNumber: string
  projectName: string
  type: LienWaiverType
  throughDate: Moment
  subcontractorName: string
  subTierVendorName?: string
}): string {
  let baseSubject = lastSubject
  if (!baseSubject) {
    baseSubject = defaultLienWaiverRequestSubject({
      projectNumber,
      projectName,
      type,
      throughDate,
      subcontractorName,
      subTierVendorName,
    })
  }
  if (isRejected) {
    // Avoid adding multiple "Rejected" prefixes, if one is already included in the subject
    return baseSubject.includes(REJECTED_PREFIX) ? baseSubject : `${REJECTED_PREFIX} ${baseSubject}`
  } else if (isReminder) {
    // Avoid adding multiple "Reminder" prefixes, if one is already included in the subject
    return baseSubject.includes(REMINDER_PREFIX) ? baseSubject : `${REMINDER_PREFIX} ${baseSubject}`
  }
  return baseSubject
}

/**
 * Returns an aggregate status for a list of lien waiver statuses, based on the status of the lien
 * request furthest from being completed
 */
export function getAggregateLienWaiverStatus(
  allStatuses: LegalDocumentStatus[]
): AggregateLienWaiverStatus {
  // Filter out lien waivers that haven't been requested (or uploaded), since we don't count them
  // in the tracker-level totals
  const statuses = allStatuses.filter((status) => status !== LegalDocumentStatus.NOT_REQUESTED)
  if (statuses.length === 0) {
    return AggregateLienWaiverStatus.NONE_REQUESTED
  }
  if (
    statuses.includes(LegalDocumentStatus.REQUESTED) ||
    statuses.includes(LegalDocumentStatus.REMINDED)
  ) {
    return AggregateLienWaiverStatus.REQUESTED
  }
  if (statuses.includes(LegalDocumentStatus.VIEWED)) {
    return AggregateLienWaiverStatus.VIEWED
  }
  if (statuses.includes(LegalDocumentStatus.NEEDS_REVIEW)) {
    return AggregateLienWaiverStatus.NEEDS_REVIEW
  }
  if (statuses.includes(LegalDocumentStatus.REJECTED)) {
    return AggregateLienWaiverStatus.REJECTED
  }
  if (
    statuses.every((status) =>
      [LegalDocumentStatus.SUBMITTED, LegalDocumentStatus.SYNCED].includes(status)
    )
  ) {
    return AggregateLienWaiverStatus.SUBMITTED
  }
  return AggregateLienWaiverStatus.SIGNED
}

/**
 * Finds the number value from a lien waiver amount input.
 * Note that the lien waiver input is of type string, and the user can enter anything they want.
 * The most likely case of the string being a non-number value is if the input defaults to something like
 * "3.00 - paid to date". This is a value that we apply to the input if the user checks the "paid to date"
 * box in the RequestLienWaiversDialog.
 *
 *
 * Returns number if exists
 * Returns undefined if an amount was not entered
 * Returns undefined if the string is "paid to date" (this suggests that the user has not entered an amount)
 * Returns null if the string cannot be parsed
 *
 * @example
 * getLienWaiverAmountFromTextEntry('3.00 - paid to date') // 300
 * getLienWaiverAmountFromTextEntry('3.00 - hello') // 300
 * getLienWaiverAmountFromTextEntry('3.00 and any other note') // 300
 * getLienWaiverAmountFromTextEntry('3.00') // 300
 * getLienWaiverAmountFromTextEntry('-3.00') // -300
 * getLienWaiverAmountFromTextEntry('-$3.00') // -300
 * getLienWaiverAmountFromTextEntry('$-3.00') // -300
 * getLienWaiverAmountFromTextEntry('') // undefined
 * getLienWaiverAmountFromTextEntry('paid to date') // undefined
 * getLienWaiverAmountFromTextEntry('hello') // null
 */
export function getLienWaiverAmountFromTextEntry(value: string): number | null | undefined {
  const trimmed = value
    // Remove all dollar signs and commas
    .replace(/[$,]/g, '')
    // Trim
    .trim()

  // If string is empty, no amount was entered and we return undefined
  if (trimmed === '') {
    return undefined
  }

  // We add "paid to date" or " - paid to date" to the text entry field if the user checks the "paid to date" in the
  // request lien waivers dialog. Treat these as non-entered amounts and return undefined.
  if (trimmed.toLowerCase() === 'paid to date') {
    return undefined
  }

  // Match a valid number with an optional leading negative sign
  const match = trimmed.match(/-?[0-9]+(\.[0-9]*)?/)
  if (!match) {
    return null
  }

  // Attempt to parse the string as a float. If it's not a valid number, return null.
  const parsed = parseFloat(match[0])
  if (isNaN(parsed)) {
    return null
  }

  // Convert the float to cents
  const amount = dollarsToCents(parsed)
  return amount
}
